/**
 * Honest Grapes — WineChap (unified shop + cellar widget)
 *
 * Usage — place before </body> in theme.liquid:
 *   <script src="{{ 'winechap.js' | asset_url }}" defer></script>
 *
 * Usage — on the cellar dashboard Liquid section, BEFORE the script tag above
 * (or in the section's own <script> block):
 *   <script>
 *     window.WineChapConfig = {
 *       mode:  'cellar',
 *       wines: [] // populated by wine-cellar-dashboard.js via window.wineCellarData
 *     };
 *   </script>
 *
 * Page awareness — add to theme.liquid BEFORE the winechap.js script tag:
 *   <script>
 *     window.WineChapPage = {
 *       type: {{ request.page_type | json }},
 *       {%- if product %} product: { handle: {{ product.handle | json }}, title: {{ product.title | json }}, variantId: {{ product.selected_or_first_available_variant.id | json }} },{% endif %}
 *       {%- if collection %} collection: { handle: {{ collection.handle | json }}, title: {{ collection.title | json }} },{% endif %}
 *     };
 *   </script>
 * Without it, the widget falls back to reading /products/{handle} or /collections/{handle} from the URL.
 *
 * Customer sign-in — add to theme.liquid BEFORE the winechap.js script tag. Without it the
 * widget treats everyone as anonymous (history stays in the browser only):
 *   {%- if customer -%}
 *     {%- assign wc_ts = 'now' | date: '%s' -%}
 *     {%- capture wc_msg -%}{{ customer.id }}:{{ wc_ts }}{%- endcapture -%}
 *     <script>window.WineChapAuth = { id: "{{ customer.id }}", ts: "{{ wc_ts }}", sig: "{{ wc_msg | hmac_sha256: shop.metafields.hg.winechap_hmac_secret.value }}" };</script>
 *   {%- endif -%}
 *
 * If window.WineChapConfig is absent or mode !== 'cellar', shop mode is used.
 * Cellar wines are persisted in localStorage for 24h so they remain available
 * when the customer navigates away from the account page.
 *
 * Depends on: Montserrat already loaded by the HG theme.
 * Worker: https://hg-shop-assistant.simon-4b6.workers.dev
 */

(function () {
  'use strict';

  const WORKER_URL  = 'https://hg-shop-assistant.simon-4b6.workers.dev';
  const STORAGE_KEY = 'hg_winechap_v1';
  const CELLAR_KEY  = 'hg_winechap_cellar';
  const CELLAR_TTL  = 24 * 60 * 60 * 1000; // 24 hours
  const HISTORY_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days

  // ── Cellar persistence ─────────────────────────────────────────────────────
  function saveCellarWines(wines) {
    try {
      localStorage.setItem(CELLAR_KEY, JSON.stringify({ wines, ts: Date.now() }));
    } catch (_) {}
  }

  function loadCellarWines() {
    try {
      const raw = localStorage.getItem(CELLAR_KEY);
      if (!raw) return [];
      const data = JSON.parse(raw);
      if (!data.ts || Date.now() - data.ts > CELLAR_TTL) {
        localStorage.removeItem(CELLAR_KEY);
        return [];
      }
      return Array.isArray(data.wines) ? data.wines : [];
    } catch (_) { return []; }
  }

  // ── Config ─────────────────────────────────────────────────────────────────
  function getConfig() {
    const cfg = window.WineChapConfig || {};
    // If on the cellar page, save wines to localStorage for cross-page persistence
    const liveWines = cfg.wines || window.wineCellarData || [];
    if (cfg.mode === 'cellar' && liveWines.length) {
      saveCellarWines(liveWines);
    }
    // Fall back to persisted cellar wines if not on the cellar page
    const persistedWines = loadCellarWines();
    return {
      isCellar: cfg.mode === 'cellar' || persistedWines.length > 0,
      wines:    liveWines.length ? liveWines : persistedWines,
    };
  }

  // ── Customer ID ────────────────────────────────────────────────────────────
  // Signed customer identity rendered by theme.liquid. The worker only trusts a customer ID
  // that comes with a valid signature, so nothing here is read from ShopifyAnalytics.
  function getCustomerAuth() {
    const a = window.WineChapAuth;
    if (!a || !/^\d+$/.test(String(a.id || '')) || !/^\d+$/.test(String(a.ts || '')) || !/^[0-9a-f]{64}$/i.test(String(a.sig || ''))) return null;
    return { id: String(a.id), ts: String(a.ts), sig: String(a.sig) };
  }

  function getCustomerId() {
    return getCustomerAuth()?.id || null;
  }

  function authHeaders() {
    const a = getCustomerAuth();
    return a ? { 'X-Customer-Id': a.id, 'X-Customer-Ts': a.ts, 'X-Customer-Sig': a.sig } : {};
  }

  function getAnonId() {
    try {
      let id = localStorage.getItem('hg_wc_anon_id');
      if (!id) {
        id = 'anon_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
        localStorage.setItem('hg_wc_anon_id', id);
      }
      return id;
    } catch (_) { return null; }
  }

  // ── Page context ───────────────────────────────────────────────────────────
  // Tells the worker which product or collection the customer is looking at.
  // The worker looks the product up in its own catalogue, so only identifiers are sent.
  function getPageContext() {
    const p     = window.WineChapPage || {};
    const path  = window.location.pathname;
    const prodM = path.match(/\/products\/([^/?#]+)/);
    const colM  = path.match(/\/collections\/([^/?#]+)/);
    const h1    = (document.querySelector('h1')?.textContent || '').trim().slice(0, 150);

    const productHandle = p.product?.handle || (prodM ? decodeURIComponent(prodM[1]) : '');
    const ctx = { type: p.type || (productHandle ? 'product' : colM ? 'collection' : 'other') };

    if (productHandle) {
      ctx.product = {
        handle: productHandle,
        title:  p.product?.title || window.ShopifyAnalytics?.meta?.product?.title || h1,
      };
      // Selected variant (Duty Paid / In Bond, bottle / case): the URL ?variant= is updated by the
      // theme when the customer switches; fall back to the add-to-cart form, then the Liquid default
      const urlVariant  = new URLSearchParams(window.location.search).get('variant');
      const formVariant = document.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
      const variantId   = urlVariant || formVariant || p.product?.variantId;
      if (variantId && /^\d+$/.test(String(variantId))) ctx.product.variantId = String(variantId);
    } else if (p.collection?.handle || colM) {
      const handle = p.collection?.handle || decodeURIComponent(colM[1]);
      if (handle !== 'all') ctx.collection = { handle, title: p.collection?.title || h1 };
    }
    return ctx;
  }

  // Short page tag saved with each message, so the worker knows which wine "this" meant at the time
  function getPageTag() {
    const c = getPageContext();
    if (c.product)    return { handle: c.product.handle,    title: c.product.title };
    if (c.collection) return { handle: c.collection.handle, title: c.collection.title };
    return null;
  }

  // ── Local storage ──────────────────────────────────────────────────────────
  function loadLocalHistory() {
    try {
      const raw    = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const cutoff = Date.now() - HISTORY_TTL;
      return (JSON.parse(raw).messages || []).filter(m => !m.ts || m.ts > cutoff);
    } catch (_) { return []; }
  }

  function saveLocalHistory(messages) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages })); } catch (_) {}
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  function esc(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  // ── Styles ─────────────────────────────────────────────────────────────────
  function injectStyles(isCellar) {
    if (document.getElementById('hg-winechap-styles')) return;
    const s  = document.createElement('style');
    s.id     = 'hg-winechap-styles';
    s.textContent = `
      /*#hg-wc-bubble {
        position: fixed; bottom: 24px; right: 24px; z-index: 99;
      }
      #hg-wc-toggle {
        width: 120px; height: 120px; border-radius: 50%;
        background: #1e4c71; color: #fff; border: none; cursor: pointer;
        font-family: 'Montserrat', sans-serif;
        font-size: 13px; font-weight: 400; line-height: 1.4;
        text-align: center; padding: 16px;
        box-shadow: 0 4px 16px rgba(125,28,39,0.35);
        transition: background 0.2s, transform 0.2s;
      }
      #hg-wc-toggle:hover { background: #1e4c71; transform: scale(1.04); }
      */
     #hg-wc-bubble {
  position: fixed; bottom: 28px; right: 28px; z-index: 99;
}
#hg-wc-toggle {
  background: #1E4C71; color: #fff; border: none; border-radius: 28px;
  padding: 5px 20px 5px 5px;
  font-family: 'Montserrat', sans-serif;
  font-size: 13px; font-weight: 400; letter-spacing: 0.05em;
  cursor: pointer; display: flex; align-items: center; gap: 11px;
  box-shadow: 0 4px 16px rgba(30,76,113,0.30);
  transition: background 0.2s, transform 0.2s; white-space: nowrap;
}
#hg-wc-toggle:hover { background: #163a5a; transform: scale(1.03); }
#hg-wc-toggle img {
  width: 38px; height: 38px; border-radius: 50%;
  object-fit: cover; object-position: top center;
  display: block; flex-shrink: 0;
}

      #hg-wc-window {
        /* Above third-party overlays such as Klaviyo forms (z-index 90000) once the customer opens the chat */
        position: fixed; z-index: 2147483000;
        background: #fff; border: 1px solid #d6d2cd; border-radius: 3px;
        box-shadow: 0 8px 40px rgba(30,76,113,0.18);
        display: none; flex-direction: column;
        font-family: 'Montserrat', sans-serif; font-size: 16px; overflow: hidden;
        box-sizing: border-box;
        ${isCellar
          ? 'bottom: 100px; right: 20px; width: min(50vw, 700px); min-width: 400px; height: 80vh; min-height: 400px; max-width: 90vw; max-height: 90vh; resize: both; overflow: auto; transform: scaleX(-1);'
          : 'bottom: 160px; right: 24px; width: min(66vw, 900px); max-width: calc(100vw - 48px); height: 70vh; max-height: calc(100vh - 190px); min-height: 500px;'
        }
      }
      #hg-wc-window.hg-open { display: flex; }

      .hg-wc-header {
        background: #1E4C71; color: #fff;
        padding: 14px 18px 12px; flex-shrink: 0;
        ${isCellar ? 'cursor: move;' : ''}
      }
      .hg-wc-header-row {
        display: flex; justify-content: space-between; align-items: flex-start;
        margin-bottom: 5px;
      }
      .hg-wc-header h3 {
        font-size: 13px; font-weight: 400; letter-spacing: 0.08em;
        text-transform: uppercase; margin: 0; color: #fff;
      }
      .hg-wc-header p {
        font-size: 12px; opacity: 0.9; line-height: 1.55;
        font-weight: 300; margin: 0; max-width: 88%;
      }
      .hg-wc-header p a { color: #fff !important; text-decoration: underline; }
      #hg-wc-close {
        background: transparent; border: none; color: #fff;
        font-size: 22px; cursor: pointer; line-height: 1;
        opacity: 0.8; padding: 0; font-family: sans-serif;
      }
      #hg-wc-close:hover { opacity: 1; }

      /* History button — cellar mode only */
      #hg-wc-history-btn {
        position: absolute; top: 15px; right: 50px;
        background: transparent; border: 1px solid rgba(255,255,255,0.5);
        color: #fff; padding: 4px 10px; border-radius: 3px;
        cursor: pointer; font-family: 'Montserrat', sans-serif;
        font-size: 11px; font-weight: 300; opacity: 0.9;
        transition: opacity 0.2s;
      }
      #hg-wc-history-btn:hover { opacity: 1; background: rgba(255,255,255,0.15); }

      /* Suggestion chips — shop mode only */
      .hg-wc-suggestions {
        padding: 8px 12px; display: flex; flex-wrap: wrap; gap: 6px;
        flex-shrink: 0; background: #fff; border-bottom: 1px solid #d6d2cd;
      }
      .hg-wc-sug {
        font-size: 12px; padding: 5px 11px;
        border: 1px solid #d6d2cd; border-radius: 2px;
        cursor: pointer; color: #1E4C71; background: #f5f3f0;
        font-family: 'Montserrat', sans-serif; transition: border-color 0.15s;
      }
      .hg-wc-sug:hover { border-color: #1E4C71; background: #fff; }

      #hg-wc-messages {
        flex: 1; overflow-y: auto; padding: 14px;
        display: flex; flex-direction: column; gap: 10px;
        background: #f5f3f0;
      }
      .hg-wc-msg {
        max-width: 80%; padding: 11px 14px;
        font-size: 14px; line-height: 1.6; border-radius: 2px;
      }
      .hg-wc-msg.hg-user {
        background: #1E4C71; color: #fff;
        align-self: flex-end; margin-left: auto;
      }
      .hg-wc-msg.hg-bot {
        background: #fff; color: #0E0209;
        align-self: flex-start; border: 1px solid #d6d2cd;
      }
      .hg-wc-msg.hg-thinking {
        background: #fff; border: 1px solid #d6d2cd;
        color: #aaa; align-self: flex-start;
        font-style: italic; font-size: 13px;
      }
      .hg-wc-dots { display: inline-block; animation: hg-pulse 1.4s infinite; }
      @keyframes hg-pulse { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }

      /* Wine cards */
      .hg-wc-cards { margin-top: 10px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
      .hg-wc-card {
        background: #fff !important; border: 1px solid #d6d2cd !important;
        border-radius: 2px; display: flex !important; flex-direction: column !important;
        overflow: hidden; text-decoration: none !important; color: inherit !important;
        transition: border-color 0.15s, box-shadow 0.15s;
      }
      .hg-wc-card:hover { border-color: #1E4C71 !important; box-shadow: 0 2px 8px rgba(30,76,113,0.10) !important; }
      .hg-wc-card-img-wrap {
        width: 100%; height: 120px; background: #fff !important;
        display: flex; align-items: center; justify-content: center;
        flex-shrink: 0; border-bottom: 1px solid #ece9e5; padding: 6px 0;
      }
      .hg-wc-card-img-wrap img {
        height: 95px !important; width: auto !important; max-width: 72% !important;
        object-fit: contain !important; background: transparent !important;
        box-shadow: none !important; border: none !important;
      }
      .hg-wc-card-body { padding: 10px; display: flex; flex-direction: column; flex: 1; }
      .hg-wc-card-name { font-size: 13px !important; font-weight: 400 !important; color: #1E4C71 !important; line-height: 1.35; margin-bottom: 3px; text-decoration: none !important; }
      .hg-wc-card-meta { font-size: 10px !important; color: #999 !important; margin-bottom: 3px; }
      .hg-wc-card-note { font-size: 11px; color: #555; font-style: italic; line-height: 1.5; margin-bottom: 5px; }
      .hg-wc-card-price { font-size: 13px !important; font-weight: 500 !important; margin-top: auto; padding-top: 4px; }
      .hg-wc-food-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 5px; }
      .hg-wc-food-tag { font-size: 10px; background: #f5f3f0; color: #1E4C71; padding: 2px 6px; border-radius: 2px; }
      .hg-wc-inbond-note { font-size: 10px; color: #c9a96e; font-style: italic; margin-top: 3px; }

      /* Input */
      .hg-wc-input-row {
        padding: 10px 12px; border-top: 1px solid #d6d2cd;
        display: flex; gap: 8px; background: #fff; flex-shrink: 0;
      }
      #hg-wc-input {
        flex: 1; font-family: 'Montserrat', sans-serif;
        font-size: 13px; font-weight: 300; padding: 8px 12px;
        border: 1px solid #d6d2cd; border-radius: 2px;
        background: #f5f3f0; color: #0E0209; outline: none;
      }
      #hg-wc-input:focus { border-color: #1E4C71; background: #fff; }
      #hg-wc-send {
        background: #1E4C71; color: #fff; border: none; border-radius: 2px;
        padding: 8px 16px; cursor: pointer; font-family: 'Montserrat', sans-serif;
        font-size: 12px; letter-spacing: 0.07em; transition: background 0.2s; white-space: nowrap;
      }
      #hg-wc-send:hover { background: #163a5a; }
      #hg-wc-send:disabled { opacity: 0.4; cursor: not-allowed; }

      /* History modal */
      .hg-wc-modal-overlay {
        display: none; position: fixed; inset: 0;
        background: rgba(0,0,0,0.5); z-index: 2147483001;
      }
      .hg-wc-modal-overlay.open { display: block; }
      .hg-wc-modal {
        display: none; position: fixed; top: 50%; left: 50%;
        transform: translate(-50%,-50%);
        width: 600px; max-width: 90vw; max-height: 80vh;
        background: #fff; border-radius: 3px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.25);
        z-index: 2147483002; flex-direction: column;
        font-family: 'Montserrat', sans-serif; font-size: 13px;
      }
      .hg-wc-modal.open { display: flex; }
      .hg-wc-modal-header {
        background: #1E4C71; color: #fff; padding: 16px 20px;
        display: flex; justify-content: space-between; align-items: center;
        border-radius: 3px 3px 0 0; flex-shrink: 0;
      }
      .hg-wc-modal-header h3 { margin: 0; font-size: 14px; font-weight: 400; color: #fff; letter-spacing: 0.06em; text-transform: uppercase; }
      .hg-wc-modal-close { background: transparent; border: none; color: #fff; font-size: 22px; cursor: pointer; opacity: 0.8; padding: 0; line-height: 1; }
      .hg-wc-modal-close:hover { opacity: 1; }
      .hg-wc-modal-body { flex: 1; overflow-y: auto; padding: 16px; }
      .hg-wc-modal-footer { padding: 12px 20px; border-top: 1px solid #e0e0e0; display: flex; gap: 8px; flex-shrink: 0; }
      .hg-wc-modal-btn {
        flex: 1; padding: 9px; background: #1E4C71; color: #fff; border: none;
        border-radius: 2px; cursor: pointer; font-family: 'Montserrat', sans-serif; font-weight: 300; font-size: 12px;
      }
      .hg-wc-modal-btn:hover { background: #163a5a; }
      .hg-history-item { margin-bottom: 16px; border: 1px solid #e0e0e0; border-radius: 2px; overflow: hidden; }
      .hg-history-item-date { padding: 6px 10px; background: #f5f3f0; font-size: 11px; color: #888; display: flex; justify-content: space-between; align-items: center; }
      .hg-history-item-q { padding: 10px; background: #edf2f7; border-bottom: 1px solid #ddd; font-size: 12px; }
      .hg-history-item-a { padding: 10px; font-size: 12px; line-height: 1.5; }
      .hg-history-del {
        background: #c33; color: #fff; border: none; padding: 2px 8px; border-radius: 2px;
        cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 300;
      }
      .hg-history-del:hover { background: #a22; }

      /* Cellar mode: flip container so resize handle is on the left, flip children back */
      #hg-wc-window.hg-cellar-mode > * { transform: scaleX(-1); }

      /* Touch devices: iOS browsers (Safari and Chrome) zoom the page when an input under 16px is focused */
      @media (pointer: coarse) {
        #hg-wc-input { font-size: 16px; }
      }

      /* Phones: full-screen chat panel. Height and top are kept in step with the visible area
         above the keyboard by fitToViewport() in JS; 100dvh is the fallback. */
      @media (max-width: 768px) {
        #hg-wc-window {
          top: 0; left: 0; right: 0; bottom: auto;
          width: 100%; max-width: none; min-width: 0;
          height: 100vh; height: 100dvh; max-height: none; min-height: 0;
          border: none; border-radius: 0; box-shadow: none;
          resize: none; transform: none; overflow: hidden; font-size: 15px;
        }
        #hg-wc-window.hg-cellar-mode > * { transform: none; }
        .hg-wc-header { cursor: default; padding: 12px 14px 10px; }
        .hg-wc-header-row { align-items: center; margin-bottom: 0; }
        .hg-wc-header p { display: none; }
        #hg-wc-close { font-size: 30px; padding: 2px 8px; margin-right: -8px; }
        #hg-wc-history-btn { position: static; margin-left: auto; margin-right: 10px; }
        .hg-wc-suggestions {
          flex-wrap: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch;
          scrollbar-width: none; padding: 8px 10px;
        }
        .hg-wc-suggestions::-webkit-scrollbar { display: none; }
        .hg-wc-sug { flex-shrink: 0; white-space: nowrap; font-size: 13px; padding: 7px 12px; }
        #hg-wc-messages { overscroll-behavior: contain; -webkit-overflow-scrolling: touch; padding: 12px; }
        .hg-wc-msg { max-width: 88%; }
        .hg-wc-input-row { padding: 8px 10px calc(8px + env(safe-area-inset-bottom)); }
        #hg-wc-input { min-width: 0; padding: 10px 12px; }
        #hg-wc-send { padding: 10px 14px; }
        #hg-wc-bubble { right: 12px; bottom: 12px; }
        #hg-wc-toggle { font-size: 12px; padding: 4px 14px 4px 4px; }
        #hg-wc-toggle img { width: 32px; height: 32px; }
        .hg-wc-cards { grid-template-columns: 1fr !important; }
        /* While open: hide the bubble and stop the page behind from scrolling */
        html.hg-wc-open, html.hg-wc-open body { overflow: hidden !important; }
        html.hg-wc-open #hg-wc-bubble { display: none; }
      }
    `;
    document.head.appendChild(s);
  }

  // ── Build DOM ──────────────────────────────────────────────────────────────
  function buildWidget(isCellar) {
    if (document.getElementById('hg-wc-bubble')) return;
    injectStyles(isCellar);

    const headerDesc = isCellar
      ? `Think of this as Head Wine Guru Tom in your pocket — ask about your wines, food pairings, drinking windows, or what to open soon. <a href="mailto:info@honestgrapes.co.uk">Email us</a> for general enquiries.`
      : `Head Wine Guru Tom in your pocket — ask about food pairings, drinking windows, what to buy for an upcoming occasion - or log in to ask about your Club Reserves`;

    const historyBtn = isCellar
      ? `<button id="hg-wc-history-btn">History</button>` : '';

    const onProduct = !!getPageContext().product;
    const suggestions = (!isCellar && onProduct) ? `
      <div class="hg-wc-suggestions" id="hg-wc-sugs">
        <button class="hg-wc-sug" data-q="What food would this go well with?">What to eat with this?</button>
        <button class="hg-wc-sug" data-q="Is this ready to drink now, or should I keep it?">Drink now or keep?</button>
        <button class="hg-wc-sug" data-q="Can you suggest something similar but cheaper?">Similar, but cheaper</button>
        <button class="hg-wc-sug" data-q="What's special about this wine?">Tell me about this wine</button>
      </div>` : !isCellar ? `
      <div class="hg-wc-suggestions" id="hg-wc-sugs">
        <button class="hg-wc-sug" data-q="What would go well with roast lamb?">Roast lamb?</button>
        <button class="hg-wc-sug" data-q="Recommend something ready to drink under £30">Under £30 tonight</button>
        <button class="hg-wc-sug" data-q="Special occasion red wine — something memorable">Special occasion red</button>
        <button class="hg-wc-sug" data-q="What Burgundy do you have in stock right now?">Burgundy?</button>
        <button class="hg-wc-sug" data-q="A selection for a dinner party, ideally a red and a white">Dinner party picks</button>
      </div>` : '';

    const historyModal = isCellar ? `
      <div class="hg-wc-modal-overlay" id="hg-wc-hist-overlay"></div>
      <div class="hg-wc-modal" id="hg-wc-hist-modal">
        <div class="hg-wc-modal-header">
          <h3>Your Chat History</h3>
          <button class="hg-wc-modal-close" id="hg-wc-hist-close">×</button>
        </div>
        <div class="hg-wc-modal-body" id="hg-wc-hist-body"><p style="color:#aaa;text-align:center;padding:2em 0">Loading…</p></div>
        <div class="hg-wc-modal-footer">
          <button class="hg-wc-modal-btn" id="hg-wc-hist-csv">Download CSV</button>
          <button class="hg-wc-modal-btn" id="hg-wc-hist-clear" style="background:#1E4C71;">Clear All</button>
        </div>
      </div>` : '';

    document.body.insertAdjacentHTML('beforeend', `
      <div id="hg-wc-bubble">
        <button id="hg-wc-toggle">
  <img src="https://www.honestgrapes.co.uk/cdn/shop/files/tom-harrow.gif?v=1688036432&width=80" alt="Tom Harrow">
  ${isCellar ? 'Chat with WineChap' : 'Ask WineChap'}
</button>
      </div>
      <div id="hg-wc-window" role="dialog" aria-label="WineChap wine assistant"${isCellar ? ' class="hg-cellar-mode"' : ''}>
        <div class="hg-wc-header" id="hg-wc-header">
          <div class="hg-wc-header-row">
            <h3>WineChap</h3>
            ${historyBtn}
            <button id="hg-wc-close" aria-label="Close">×</button>
          </div>
          <p>${headerDesc}</p>
        </div>
        ${suggestions}
        <div id="hg-wc-messages"></div>
        <div class="hg-wc-input-row">
          <input id="hg-wc-input" type="text" placeholder="${isCellar ? 'Ask about your wines…' : onProduct ? 'Ask about this wine…' : 'Ask about wines, food, occasions…'}" autocomplete="off" maxlength="500">
          <button id="hg-wc-send">Send</button>
        </div>
      </div>
      ${historyModal}
    `);

    // Draggable header for cellar mode
    if (isCellar) {
      const header    = document.getElementById('hg-wc-header');
      const win       = document.getElementById('hg-wc-window');
      let dragging    = false, ix = 0, iy = 0;
      header.addEventListener('mousedown', e => {
        if (isPhone()) return;
        if (e.target.tagName === 'BUTTON' || e.target.tagName === 'A') return;
        dragging = true; ix = e.clientX - win.offsetLeft; iy = e.clientY - win.offsetTop;
      });
      document.addEventListener('mousemove', e => {
        if (!dragging) return;
        e.preventDefault();
        win.style.left = (e.clientX - ix) + 'px'; win.style.top = (e.clientY - iy) + 'px';
        win.style.right = 'auto'; win.style.bottom = 'auto';
      });
      document.addEventListener('mouseup', () => { dragging = false; });
    }

    initEvents(isCellar);
  }

  // ── Mobile helpers ─────────────────────────────────────────────────────────
  const isPhone = () => window.matchMedia('(max-width: 768px)').matches;
  const isTouch = () => window.matchMedia('(pointer: coarse)').matches;

  // On phones the panel fills the visible area. iOS doesn't shrink the layout viewport when the
  // keyboard opens, so size the panel to visualViewport instead, which does.
  function fitToViewport() {
    const win = document.getElementById('hg-wc-window');
    if (!win) return;
    const vv = window.visualViewport;
    if (!isPhone() || !win.classList.contains('hg-open') || !vv) {
      win.style.height = ''; win.style.top = '';
      return;
    }
    win.style.height = vv.height + 'px';
    win.style.top    = vv.offsetTop + 'px';
    const msgs = document.getElementById('hg-wc-messages');
    if (msgs && document.activeElement && document.activeElement.id === 'hg-wc-input') msgs.scrollTop = msgs.scrollHeight;
  }

  function setOpenState(open) {
    document.documentElement.classList.toggle('hg-wc-open', open && isPhone());
    fitToViewport();
  }

  // ── State ──────────────────────────────────────────────────────────────────
  let conversationHistory = [];
  let historyLoaded       = false;

  // ── Events ─────────────────────────────────────────────────────────────────
  function initEvents(isCellar) {
    const toggle = document.getElementById('hg-wc-toggle');
    const win    = document.getElementById('hg-wc-window');
    const close  = document.getElementById('hg-wc-close');
    const input  = document.getElementById('hg-wc-input');
    const send   = document.getElementById('hg-wc-send');

    const closeWin = () => { win.classList.remove('hg-open'); input.blur(); setOpenState(false); };
    toggle.addEventListener('click', () => {
      const isOpen = win.classList.toggle('hg-open');
      setOpenState(isOpen);
      if (isOpen) {
        if (!historyLoaded) { historyLoaded = true; loadAndRenderHistory(isCellar); }
        if (!isTouch()) input.focus(); // on phones, let the customer tap the box when ready
      }
    });
    close.addEventListener('click', closeWin);
    document.addEventListener('click', e => {
      if (!win.classList.contains('hg-open')) return;
      if (win.contains(e.target) || toggle.contains(e.target)) return;
      // The history modal sits outside the window; clicks in it shouldn't close the chat
      if (e.target.closest && e.target.closest('#hg-wc-hist-modal, #hg-wc-hist-overlay')) return;
      closeWin();
    });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', fitToViewport);
      window.visualViewport.addEventListener('scroll', fitToViewport);
    }
    window.addEventListener('resize', () => setOpenState(win.classList.contains('hg-open')));
    send.addEventListener('click', () => handleSend(isCellar));
    input.addEventListener('keydown', e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(isCellar); } });

    // Suggestion chips (shop mode only)
    document.getElementById('hg-wc-sugs')?.querySelectorAll('.hg-wc-sug').forEach(btn => {
      btn.addEventListener('click', () => { input.value = btn.dataset.q; handleSend(isCellar); });
    });

    // History modal (cellar mode only)
    if (isCellar) {
      const histBtn     = document.getElementById('hg-wc-history-btn');
      const histOverlay = document.getElementById('hg-wc-hist-overlay');
      const histModal   = document.getElementById('hg-wc-hist-modal');
      const histClose   = document.getElementById('hg-wc-hist-close');
      const histCsv     = document.getElementById('hg-wc-hist-csv');
      const openModal   = () => { histModal.classList.add('open'); histOverlay.classList.add('open'); loadHistoryModal(); };
      const closeModal  = () => { histModal.classList.remove('open'); histOverlay.classList.remove('open'); };
      histBtn.addEventListener('click', openModal);
      histClose.addEventListener('click', closeModal);
      histOverlay.addEventListener('click', closeModal);
      histCsv.addEventListener('click', downloadCSV);
      document.getElementById('hg-wc-hist-clear').addEventListener('click', clearAllHistory);
    }
  }

  // ── Load + render history on open ──────────────────────────────────────────
  async function loadAndRenderHistory(isCellar) {
    const customerId = getCustomerId();
    const local      = loadLocalHistory();
    let merged       = [...local];

    if (customerId) {
      try {
        const endpoint = isCellar
          ? `${WORKER_URL}/questions/${customerId}`
          : `${WORKER_URL}/history?user_id=${customerId}`;
        const r = await fetch(endpoint, { headers: { ...authHeaders(), 'X-Anon-Id': getAnonId() || '' } });
        if (r.ok) {
          const data        = await r.json();
          const serverConvs = data.conversations || [];
          const localQs     = new Set(local.map(m => m.question));
          const todayStart  = new Date(); todayStart.setHours(0,0,0,0);
          serverConvs.forEach(c => {
            const ts = new Date(c.timestamp).getTime();
            if (ts >= todayStart.getTime() && !localQs.has(c.question)) {
              merged.push({ question: c.question, response: c.response || '', wines: [], ts, page: c.page ? { handle: c.page.handle, title: c.page.title } : null });
              localQs.add(c.question);
            }
          });
          merged.sort((a, b) => (a.ts || 0) - (b.ts || 0));
        }
      } catch (_) {}
    }

    if (!merged.length) {
      addMessage('bot', "Hello — I'm WineChap, your guide to the Honest Grapes cellar. Ask me anything: what to open tonight, which wines go with what you're cooking, or what's worth laying down. What can I find for you?");
      return;
    }

    // Rebuild today's conversationHistory
    const todayTs = (() => { const d = new Date(); d.setHours(0,0,0,0); return d.getTime(); })();
    merged.filter(m => m.ts && m.ts >= todayTs).forEach(m => {
      conversationHistory.push({ role: 'user',      content: m.question, page: m.page || null });
      conversationHistory.push({ role: 'assistant', content: m.response.replace(/WINE_RECS_JSON:\s*\[[\s\S]*?\]/, '').trim() });
    });

    // Show last 4 turns muted
    const msgs = document.getElementById('hg-wc-messages');
    merged.slice(-4).forEach(m => {
      const u = document.createElement('div');
      u.className = 'hg-wc-msg hg-user'; u.style.opacity = '0.65';
      u.textContent = m.question; msgs.appendChild(u);

      const prose = m.response.replace(/WINE_RECS_JSON:\s*\[[\s\S]*?\]/, '').trim();
      const b = document.createElement('div');
      b.className = 'hg-wc-msg hg-bot'; b.style.opacity = '0.65';
      b.innerHTML = esc(prose).replace(/\n/g, '<br>');
      if (Array.isArray(m.wines) && m.wines.length) b.appendChild(renderCards(m.wines));
      msgs.appendChild(b);
    });

    const sep = document.createElement('div');
    sep.style.cssText = 'text-align:center;font-size:11px;color:#aaa;padding:4px 0;font-family:Montserrat,sans-serif;';
    sep.textContent = '— previous conversation —';
    msgs.appendChild(sep);

    // If they've moved to a different wine since the last message, say so
    const nowTag  = getPageTag();
    const lastTag = merged[merged.length - 1]?.page || null;
    if (nowTag && nowTag.title && (!lastTag || lastTag.handle !== nowTag.handle)) {
      const now = document.createElement('div');
      now.style.cssText = 'text-align:center;font-size:11px;color:#1E4C71;padding:0 0 4px;font-family:Montserrat,sans-serif;';
      now.textContent = 'Now looking at: ' + nowTag.title;
      msgs.appendChild(now);
    }
    msgs.scrollTop = msgs.scrollHeight;
  }

  // ── Send ───────────────────────────────────────────────────────────────────
  async function handleSend(isCellar) {
    const input = document.getElementById('hg-wc-input');
    const send  = document.getElementById('hg-wc-send');
    const text  = input.value.trim();
    if (!text) return;

    input.value   = '';
    send.disabled = true;
    addMessage('user', text);
    const thinking = addThinking();

    // Re-read cellar wines at send-time:
    // 1. Prefer live WineChapConfig (on cellar page)
    // 2. Fall back to localStorage (persisted from a previous visit, TTL 24h)
    const liveCfg    = window.WineChapConfig || {};
    const liveWines  = (liveCfg.mode === 'cellar' && (liveCfg.wines || window.wineCellarData || []).length)
                         ? (liveCfg.wines || window.wineCellarData)
                         : null;
    const cellarWines = liveWines || loadCellarWines();
    const sendIsCellar = cellarWines.length > 0;
    const customerId   = getCustomerId();
    console.log(`WineChap send: mode=${sendIsCellar ? 'cellar' : 'shop'}, wines=${cellarWines.length}, customerId=${customerId}`);

    let result;
    try {
      const r = await fetch(`${WORKER_URL}/chat`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          ...authHeaders(),
          'X-Anon-Id':     getAnonId() || '',
        },
        body: JSON.stringify({
          message:             text,
          conversationHistory: conversationHistory,
          pageContext:         getPageContext(),
          ...(sendIsCellar ? { cellarWines } : {}),
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      result = await r.json();
    } catch (err) {
      thinking.remove();
      addMessage('bot', `Sorry — I couldn't reach the server just now. Please try again. (${err.message})`);
      send.disabled = false;
      return;
    }

    thinking.remove();
    const prose  = result.text   || '';
    const recs   = result.wines  || [];
    const events = result.events || [];

    const pageTag = getPageTag();
    conversationHistory.push({ role: 'user',      content: text, page: pageTag });
    conversationHistory.push({ role: 'assistant', content: prose });
    if (conversationHistory.length > 40) conversationHistory = conversationHistory.slice(-40);

    const stored = loadLocalHistory();
    stored.push({ question: text, response: prose, wines: recs, ts: Date.now(), page: pageTag });
    saveLocalHistory(stored.slice(-100));

    if (events.length) {
      addMessage('bot', prose);
      const evWrap = renderEventCards(events);
      const msgs = document.getElementById('hg-wc-messages');
      msgs.appendChild(evWrap);
      const offset = evWrap.offsetTop - msgs.offsetTop - 8;
      msgs.scrollTop = offset;
    } else {
      addMessage('bot', prose, recs);
    }
    send.disabled = false;
    if (!isTouch()) document.getElementById('hg-wc-input').focus();
    else document.getElementById('hg-wc-input').blur(); // drop the keyboard so the answer is visible
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  function addMessage(role, text, wines) {
    const msgs = document.getElementById('hg-wc-messages');
    const div  = document.createElement('div');
    div.className = `hg-wc-msg ${role === 'user' ? 'hg-user' : 'hg-bot'}`;
    if (role === 'bot') {
      const sendIsCellar = (window.WineChapConfig?.mode === 'cellar') || loadCellarWines().length > 0;

      let displayText = text;
      let headingText = null;
      if (wines?.length && text.trimEnd().endsWith('Further recommendations:')) {
        displayText = text.trimEnd().slice(0, -'Further recommendations:'.length).trimEnd();
        headingText = 'Further recommendations';
      } else if (wines?.length && !sendIsCellar) {
        headingText = 'Available to buy';
      }

      div.innerHTML = esc(displayText).replace(/\n/g, '<br>');
      if (wines?.length) {
        if (headingText) {
          const h = document.createElement('div');
          h.style.cssText = 'font-size:11px;font-weight:400;letter-spacing:0.07em;text-transform:uppercase;color:#1E4C71;margin:12px 0 6px;';
          h.textContent = headingText;
          div.appendChild(h);
        }
        div.appendChild(renderCards(wines));
      }
    } else {
      div.textContent = text;
    }
    msgs.appendChild(div);
    if (role === 'bot') {
      // Scroll so the top of the answer is visible, not the bottom
      var offset = div.offsetTop - msgs.offsetTop - 8;
      msgs.scrollTop = offset;
    } else {
      msgs.scrollTop = msgs.scrollHeight;
    }
    return div;
  }

  function addThinking() {
    const msgs = document.getElementById('hg-wc-messages');
    const div  = document.createElement('div');
    div.className = 'hg-wc-msg hg-thinking';
    div.innerHTML = 'Thinking <span class="hg-wc-dots">●●●</span>';
    msgs.appendChild(div);
    msgs.scrollTop = msgs.scrollHeight;
    return div;
  }

  function renderCards(wines) {
    const wrap = document.createElement('div');
    wrap.className = 'hg-wc-cards';
    wines.forEach(w => {
      const priceStr  = w.price ? `£${parseFloat(w.price).toFixed(2)}` : '';
      const metaParts = [w.region, w.grapes, w.alcohol ? `${w.alcohol}%` : null].filter(Boolean);
      const tags      = (w.dishes || []).slice(0, 3);
      const inner     = `
        <div class="hg-wc-card-img-wrap">
          <img src="${esc(w.image || '')}" alt="${esc(w.name)}" onerror="this.parentElement.style.background='#edeae6'">
        </div>
        <div class="hg-wc-card-body">
          <div class="hg-wc-card-name">${esc(w.name)}</div>
          ${metaParts.length ? `<div class="hg-wc-card-meta">${esc(metaParts.join(' · '))}</div>` : ''}
          ${w.tastingNote ? `<div class="hg-wc-card-note">${esc(w.tastingNote)}</div>` : ''}
          ${tags.length ? `<div class="hg-wc-food-tags">${tags.map(t => `<span class="hg-wc-food-tag">${esc(t)}</span>`).join('')}</div>` : ''}
          ${priceStr ? `<div class="hg-wc-card-price">${priceStr} <span style="font-size:10px;font-weight:300;color:#999">${esc(w.unit || '')}</span></div>` : ''}
          ${w.inBond ? `<div class="hg-wc-inbond-note">In bond — for investment/long-term cellaring</div>` : ''}
        </div>`;
      const el = document.createElement(w.url ? 'a' : 'div');
      el.className = 'hg-wc-card';
      if (w.url) { el.href = w.url; el.target = '_blank'; el.rel = 'noopener'; }
      el.innerHTML = inner;
      wrap.appendChild(el);
    });
    return wrap;
  }

  function renderEventCards(events) {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'margin-top:10px;display:flex;flex-direction:column;gap:8px;';
    events.forEach(function(ev) {
      // Row layout: image left (if present), content right
      const card = document.createElement(ev.url ? 'a' : 'div');
      if (ev.url) { card.href = ev.url; card.target = '_blank'; card.rel = 'noopener'; }
      card.style.cssText = 'background:#fff;border:1px solid #d6d2cd;border-radius:2px;overflow:hidden;display:flex;flex-direction:row;text-decoration:none;color:inherit;transition:border-color 0.15s;';
      card.onmouseover = function() { card.style.borderColor = '#1E4C71'; };
      card.onmouseout  = function() { card.style.borderColor = '#d6d2cd'; };

      // Image strip (left, narrow)
      if (ev.image) {
        const imgWrap = document.createElement('div');
        imgWrap.style.cssText = 'width:80px;flex-shrink:0;overflow:hidden;background:#edeae6;';
        const img = document.createElement('img');
        img.src = ev.image; img.alt = ev.name || 'Event';
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
        imgWrap.appendChild(img);
        card.appendChild(imgWrap);
      }

      // Content
      const content = document.createElement('div');
      content.style.cssText = 'flex:1;padding:10px 12px;display:flex;flex-direction:column;gap:3px;min-width:0;background:#1E4C71;';

      if (ev.category) {
        const cat = document.createElement('div');
        cat.style.cssText = 'font-size:9px;letter-spacing:0.12em;text-transform:uppercase;color:rgba(255,255,255,0.6);';
        cat.textContent = ev.category;
        content.appendChild(cat);
      }
      const name = document.createElement('div');
      name.style.cssText = 'font-size:12px;font-weight:400;color:#fff;line-height:1.3;';
      name.textContent = ev.name || 'Event';
      content.appendChild(name);

      if (ev.date) {
        const date = document.createElement('div');
        date.style.cssText = 'font-size:10px;color:#c9a96e;';
        date.textContent = ev.date;
        content.appendChild(date);
      }

      // Footer row: price + selling fast + book
      const footer = document.createElement('div');
      footer.style.cssText = 'display:flex;align-items:center;gap:8px;margin-top:4px;';

      if (ev.price) {
        const price = document.createElement('span');
        price.style.cssText = 'font-size:11px;color:#fff;';
        price.textContent = '£' + parseFloat(ev.price).toFixed(0) + '/person';
        footer.appendChild(price);
      }
      if (ev.tickets !== undefined && ev.tickets < 10) {
        const tix = document.createElement('span');
        tix.style.cssText = 'font-size:10px;color:#c9a96e;';
        tix.textContent = 'Selling fast';
        footer.appendChild(tix);
      }
      if (ev.url) {
        const book = document.createElement('span');
        book.textContent = 'Book';
        book.style.cssText = 'margin-left:auto;font-size:9px;font-weight:400;letter-spacing:0.07em;text-transform:uppercase;color:#fff;background:#7D1C27;padding:2px 7px;border-radius:2px;white-space:nowrap;';
        footer.appendChild(book);
      }
      content.appendChild(footer);
      card.appendChild(content);
      wrap.appendChild(card);
    });
    return wrap;
  }

  // ── History modal (cellar mode) ────────────────────────────────────────────
  let modalHistory = [];

  async function loadHistoryModal() {
    const userId = getCustomerId();
    const body   = document.getElementById('hg-wc-hist-body');
    if (!userId) { body.innerHTML = '<p style="color:#aaa;text-align:center;padding:2em 0">Please log in to view your chat history.</p>'; return; }
    body.innerHTML = '<p style="color:#aaa;text-align:center;padding:2em 0">Loading…</p>';
    try {
      const r = await fetch(`${WORKER_URL}/questions/${userId}`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      modalHistory = (data.conversations || []).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      if (!modalHistory.length) { body.innerHTML = '<p style="color:#aaa;text-align:center;padding:2em 0">No chat history yet.</p>'; return; }
      body.innerHTML = modalHistory.map((c, i) => {
        const d = new Date(c.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        return `
          <div class="hg-history-item">
            <div class="hg-history-item-date">
              ${d}
              <button class="hg-history-del" onclick="window._wcDelConv('${c.timestamp}')">Delete</button>
            </div>
            <div class="hg-history-item-q"><strong>Q:</strong> ${esc(c.question)}</div>
            <div class="hg-history-item-a">
              <strong>A:</strong> ${(() => {
                const raw   = c.response || '';
                const prose = raw.replace(/WINE_RECS_JSON:\s*\[[\s\S]*?\]/, '').trim();
                const jm    = raw.match(/WINE_RECS_JSON:\s*(\[[\s\S]*?\])/);
                let wines   = '';
                if (jm) {
                  try {
                    const names = JSON.parse(jm[1]).map(n => n.name).filter(Boolean);
                    if (names.length) wines = '<div style="font-size:0.7em;font-weight:400;letter-spacing:0.07em;text-transform:uppercase;color:#1E4C71;margin:10px 0 4px">Recommendations</div>'
                      + '<ul style="margin:0;padding-left:1.2em;font-size:0.95em;color:#1E4C71">'
                      + names.map(n => `<li>${esc(n)}</li>`).join('') + '</ul>';
                  } catch(_) {}
                }
                return esc(prose) + wines;
              })()}
            </div>
          </div>`;
      }).join('');
    } catch (e) {
      body.innerHTML = `<p style="color:#c33;text-align:center;padding:2em 0">Failed to load history.</p>`;
    }
  }

  window._wcDelConv = async function (timestamp) {
    const userId = getCustomerId();
    if (!userId || !confirm('Delete this conversation?')) return;
    try {
      const r = await fetch(`${WORKER_URL}/delete-conversation/${userId}/${encodeURIComponent(timestamp)}`, {
        method: 'DELETE', headers: authHeaders()
      });
      if (r.ok) {
        // Also remove from localStorage so it doesn't reappear in the widget
        try {
          const stored = loadLocalHistory();
          const ts = new Date(timestamp).getTime();
          const filtered = stored.filter(m => {
            // Match by timestamp proximity (within 1 min) since localStorage uses ms ts, server uses ISO
            return !m.ts || Math.abs(m.ts - ts) > 60000;
          });
          saveLocalHistory(filtered);
        } catch (_) {}
        loadHistoryModal();
      }
    } catch (_) {}
  };

  function downloadCSV() {
    if (!modalHistory.length) return;
    const sorted = [...modalHistory].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const csv    = [
      'Timestamp,Question,Response',
      ...sorted.map(c => { const cleanR = (c.response||'').replace(/WINE_RECS_JSON:\s*\[[\s\S]*?\]/, '').trim(); return `${c.timestamp},"${c.question.replace(/"/g,'""')}","${cleanR.replace(/"/g,'""')}"`; })
    ].join('\n');
    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url; link.download = `winechap-history-${new Date().toISOString().split('T')[0]}.csv`;
    link.click(); URL.revokeObjectURL(url);
  }

  async function clearAllHistory() {
    const userId = getCustomerId();
    if (!confirm('Delete all your WineChap chat history? This cannot be undone.')) return;
    try {
      if (userId) {
        await fetch(`${WORKER_URL}/delete-user/${userId}`, {
          method: 'DELETE', headers: authHeaders()
        }).catch(() => {});
      }
      saveLocalHistory([]);
      conversationHistory = [];
      loadHistoryModal();
      const msgs = document.getElementById('hg-wc-messages');
      if (msgs) msgs.innerHTML = '';
    } catch (_) {}
  }

  // ── Boot ───────────────────────────────────────────────────────────────────
  function boot() {
    // Poll briefly for WineChapConfig — it may be set by an inline <script> that
    // executes after this defer'd file, or by a Liquid section that loads later.
    // Also check for persisted cellar wines in localStorage.
    // Stop polling after 2s and fall back to shop mode.
    let attempts = 0;
    const check = setInterval(() => {
      attempts++;
      const cfg         = window.WineChapConfig;
      const ready       = cfg !== undefined;
      const hasPersisted = loadCellarWines().length > 0;
      console.log(`WineChap boot attempt ${attempts}: WineChapConfig=`, cfg, `persisted cellar=${hasPersisted}`);
      if (ready || hasPersisted || attempts >= 20) {
        clearInterval(check);
        const { isCellar } = getConfig();
        console.log(`WineChap building in ${isCellar ? 'cellar' : 'shop'} mode`);
        buildWidget(isCellar);
      }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();